import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,  // IMPORTANT: Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// REMOVED: Request interceptor that added Authorization header from localStorage
// The httpOnly cookie is sent automatically with withCredentials: true

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't refresh for auth endpoints themselves
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/refresh')
      || originalRequest?.url?.includes('/api/auth/login');
    if (isAuthEndpoint) {
      window.dispatchEvent(new CustomEvent('auth:logout'));
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?expired=true';
      }
      return Promise.reject(error);
    }

    // Queue concurrent 401s while refreshing
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await apiClient.post('/api/auth/refresh');
      failedQueue.forEach(p => p.resolve(undefined));
      failedQueue = [];
      isRefreshing = false;
      return apiClient(originalRequest);
    } catch (refreshError) {
      failedQueue.forEach(p => p.reject(refreshError));
      failedQueue = [];
      isRefreshing = false;
      window.dispatchEvent(new CustomEvent('auth:logout'));
      const isProfileCheck = originalRequest?.url?.includes('/api/auth/profile')
        || originalRequest?.url?.includes('/api/auth/me');
      if (window.location.pathname !== '/login' && !isProfileCheck) {
        window.location.href = '/login?expired=true';
      }
      return Promise.reject(refreshError);
    }
  }
);

export default apiClient;
